import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router";
import Detail from "../../../Detail/Detail";
import TagAutocomplete from "../../../../components/Autocomplete/TagAutocomplete/TagAutocomplete";
import DisplayValue from "../../../../components/TextField/DisplayValue/DisplayValue";
import { displayLocalDate } from "../../../../helpers/utcUtils";
import {
    detailModes as modes,
    SUBJECT,
    NOTE_DESCRIPTION_HELPER_TEXT,
    DESCRIPTION,
    CANCEL_NOTE_CREATION,
    CANCEL_NOTE_CREATION_TEXT,
    CREATED_DATE,
    CREATED_BY,
    TAGS,
    SELECT_TAGS,
    FINANCIALS,
} from "../../../../constants/constants";
import { GO_BACK, CONTINUE } from "../../../../constants/actionButtons";
import {
    NOTE_SAVE_MESSAGE,
    NOTE_CREATE_WARNING_MSG,
} from "../../../../constants/messages"
import { getService, postService } from "../../../../axios/axios";
import {
    resetNoteDetail,
    setInputValue,
    setNoteDetail,
} from "../../../../store/actions/noteActions";
import {
    Alert,
    Grid,
    GridColumn,
    TextField,
    TextArea,
    Modal,
    Button,
    ButtonGroup,
    Body,
    Divider,
    Tag,
    useSnackbar,
    Heading,
    Skeleton,
    SkeletonText
} from "@walmart-web/livingdesign-components";
import Label from "../../../../components/TextField/Label/Label";
import { getErrorMessage, renderTernary } from "../../../../helpers/utils";
import { filterPayload } from "../../../../helpers/commonUtils";
import { useForm } from "react-hook-form";
import { NOTE_SERVICE } from "../../../../constants/baseURLs";
import parse from 'html-react-parser';
import Subheading from "../../../../components/TextField/SubHeading/Subheading";
import MuiValidationMessage from "../../../../Validation/MuiValidationMessage/MuiValidationMessage";
import { notesValidationRules } from "../../../../Validation/validationRules";
import { htmlRegexCheck } from "../../../../helpers/regexUtils";
import ViewInvoiceNotesStyles from "./ViewInvoiceNotes.styles";

// Main functional component for viewing invoice notes
const ViewInvoiceNotes = () => {
    const { state: { module = "invoice", moduleDetails = {}, heading } = {} } = useLocation();
    const classes = ViewInvoiceNotesStyles();
    const history = useHistory();
    const { noteId } = useParams();
    const dispatch = useDispatch();
    const { state: { currentTab } = {} } = useLocation();
    const { addSnack } = useSnackbar();
    const [mode, setMode] = useState(null);
    const [noteCreationModalOpen, setNoteCreationModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const noteDetail = useSelector((state) => state?.note?.noteDetail);
    const noteTags = useSelector(state => state?.masterData?.masterDatas?.noteTags);
    const { register, formState: { errors, isValid } } = useForm({ mode: "all" });

    useEffect(() => {
        if (noteId === "add") {
            dispatch(resetNoteDetail());
            setMode(modes.CREATE);
            setLoading(false);
        } else {
            getNoteData();
            setMode(modes.VIEW);
        }
    }, []);

    const getNoteData = () => {
        setLoading(true)
        getService(NOTE_SERVICE, `/notes/v1/${noteId}`)
            .then((res) => {
                dispatch(setNoteDetail(res?.data))
                setLoading(false)
            }).catch((error) => {
                errorHandle(error);
                setLoading(false)
            });
    }

    /**
     * function will use for api error message
     * @param {*} msg
     */
    const errorHandle = (error) => {
        setLoading(false);
        addSnack({
            message: getErrorMessage(error),
        });
    }

    /**
     * Opens note creation modal when clicked on cancel button.
     */
    const handleCancel = () => {
        setNoteCreationModalOpen(true);
    };

    /**
     * Gets called when data changes in input fields.
     */
    const handleInputChange = (name, value) => {
        dispatch(setInputValue(name, value));
    };

    /**
     * Gets called when clicked on create button.
     */
    const handleCreate = (e) => {
        const body = {
            description: noteDetail?.description,
            title: noteDetail?.title,
            color: "blue",
            matterNumber: moduleDetails?.id,
            tags: noteDetail?.tags,
            isPrivate: false,
        };
        const filteredBody = filterPayload(body)
        setLoading(true);
        postService(NOTE_SERVICE, "/notes/v1", filteredBody)
            .then((res) => {
                setLoading(false);
                addSnack({
                    message: NOTE_SAVE_MESSAGE,
                });
                history.push(`/${module}/${moduleDetails?.id}`, { currentTab, isRowClick: true });
            })
            .catch((error) => {
                errorHandle(error);
            });
    };


    /**
     * Cancel Note Creation Modal modal.
     */
    const cancelNoteCreationModal = () => {
        return (
            <Modal
                onClose={() => setNoteCreationModalOpen(false)}
                isOpen={noteCreationModalOpen}
                actions={noteCreateModalActions()}
                size="small"
                title={CANCEL_NOTE_CREATION}
            >
                <Grid>
                    <Body as="p" size="large">
                        {CANCEL_NOTE_CREATION_TEXT}
                    </Body>
                </Grid>
            </Modal>
        );
    };

    /**
     * View mode when clicked on Go Back button.
     */
    const onClickGoBack = () => {
        setNoteCreationModalOpen(false);
    };

    /**
     * NoteCreateModalActions Actions
     */
    const noteCreateModalActions = () => {
        return (
            <Grid>
                <GridColumn sm={5}>
                    <ButtonGroup>
                        <Button
                            data-testid="goback-btn"
                            id="goback-btn"
                            size="small"
                            onClick={() => onClickGoBack()}
                        >
                            {GO_BACK}
                        </Button>
                        <Button
                            variant="destructive"
                            size="small"
                            data-testid="continue-btn"
                            id="continue-btn"
                            onClick={() => {
                                setNoteCreationModalOpen(false);
                                history.push(`/${module}/${moduleDetails?.id}`, { currentTab });
                            }}
                        >
                            {CONTINUE}
                        </Button>
                    </ButtonGroup>
                </GridColumn>
            </Grid>
        );
    };

    return (
        <>
            {renderTernary(mode === modes.CREATE, <div className={classes.warningMsg}>
                <Alert variant="warning">{NOTE_CREATE_WARNING_MSG}</Alert></div>, '')}
            <Detail
                mode={mode}
                setMode={setMode}
                breadcrumbValues={[
                    {
                        label: FINANCIALS,
                        path: `financial`
                    },
                    {
                        label: `#${moduleDetails?.organizationInvoiceNumber}`,
                        path: `invoice/${moduleDetails?.id}`,
                        currentMatterTab: currentTab
                    },
                    { label: `Note: ${noteDetail?.notesId}` },
                ]
                }
                subject={heading}
                newSubtitle={""}
                newTitle={heading ? heading : ""}
                onCancel={handleCancel}
                onCreate={handleCreate}
                hideEditBtn={true}
                hideDeleteBtn={true}
                loading={loading}
                isDirty={true}
                isValid={isValid}
            >
                {mode === modes.VIEW ? (
                    <>
                        <Grid>
                            <GridColumn sm={9}>
                                <div className={classes.leftContent}>
                                    {loading ? (
                                        <div className={classes.noteTitle}>
                                            <Skeleton height="26px" variant="rectangle" />
                                        </div>
                                    ) : (
                                        <div className={classes.noteTitle} id="note-detail-title">
                                            <Heading as="h1">
                                                {noteDetail?.title}
                                            </Heading>
                                        </div>
                                    )}
                                    {loading ? (
                                        <SkeletonText lines={6} variant="rectangle" />
                                    ) : (
                                        <>
                                            <Subheading as="p" variant="2">
                                                {DESCRIPTION}
                                            </Subheading>
                                            <div className={classes.descriptionText}>{htmlRegexCheck.test(noteDetail?.description) ? parse(noteDetail?.description.replace(/\n/g, '')) : noteDetail?.description}</div>
                                        </>
                                    )}
                                </div>
                            </GridColumn>
                            <GridColumn sm={3} style={{ fontSize: 17 }}>
                                {loading ? (
                                    <SkeletonText lines={1} variant="rectangle" />
                                ) : (
                                    <DisplayValue
                                        label={"Invoice Number"}
                                        value={
                                            moduleDetails?.organizationInvoiceNumber ? moduleDetails?.organizationInvoiceNumber : "-"
                                        }
                                    />
                                )}
                                <div className={classes.contentSpacing24}>
                                    {loading ? (
                                        <SkeletonText lines={1} variant="rectangle" />
                                    ) : (
                                        <DisplayValue
                                            label={CREATED_DATE}
                                            value={
                                                noteDetail?.createdTs
                                                    ? displayLocalDate(noteDetail?.createdTs)
                                                    : "-"
                                            }
                                        />
                                    )}
                                </div>
                                <div className={classes.contentSpacing24}>
                                    {loading ? (
                                        <SkeletonText lines={1} variant="rectangle" />
                                    ) : (
                                        <DisplayValue
                                            label={CREATED_BY}
                                            value={
                                                noteDetail && noteDetail?.createdBy
                                                    ? noteDetail?.createdBy
                                                    : "-"
                                            }
                                        />
                                    )}
                                </div>
                                <div className={classes.sideDivider}>
                                    <Divider />
                                </div>
                                <div className={classes.contentSpacing24}>
                                    {loading ? (
                                        <SkeletonText lines={1} variant="rectangle" />
                                    ) : (
                                        <DisplayValue
                                            label={TAGS}
                                            value={
                                                noteDetail?.tags?.length !== 0 ?
                                                    <>
                                                        {noteDetail?.tags.map((value, i) => (
                                                            <div className={classes.tag} key={value}>
                                                                <Tag variant="tertiary">
                                                                    {value}
                                                                </Tag>
                                                            </div>
                                                        ))}
                                                    </> :
                                                    "-"
                                            }
                                        />
                                    )}
                                </div>
                            </GridColumn>
                        </Grid>
                    </>
                ) : (
                    <>
                        <Grid data-testid="edit-content">
                            <GridColumn sm={9}>
                                <div className={classes.leftContent}>
                                    {loading ? (
                                        <Skeleton height="40px" variant="rectangle" />
                                    ) : (
                                        <React.Fragment>
                                            <Label text={`${SUBJECT}*`} />
                                            <TextField
                                                type="text"
                                                textFieldProps={{
                                                    id: "title",
                                                    name: "title",
                                                    required: true,
                                                }}
                                                data-testid="title-text"
                                                value={noteDetail?.title}
                                                error={errors?.title && <MuiValidationMessage message={errors?.title?.message} mode="LD" />}
                                                {...register(
                                                    'title',
                                                    { ...notesValidationRules?.title, onChange: (e) => { handleInputChange(e.target.name, e.target.value) } }
                                                )}
                                            />
                                        </React.Fragment>
                                    )}
                                    <div className={classes.contentSpacing16}>
                                        {loading ? (
                                            <div className={classes.contentSpacing24}>
                                                <Skeleton height="100px" variant="rectangle" />
                                            </div>
                                        ) : (
                                            <React.Fragment>
                                                <Label text={DESCRIPTION} mandatoryField={true} />
                                                <TextArea
                                                    helperText={NOTE_DESCRIPTION_HELPER_TEXT}
                                                    textAreaProps={{
                                                        id: "description",
                                                        name: "description",

                                                    }}
                                                    data-testid="description-text"
                                                    maxLength={500}
                                                    value={noteDetail?.description}
                                                    error={errors?.description && <MuiValidationMessage message={errors?.description?.message} mode="LD" />}
                                                    {...register(
                                                        'description',
                                                        { ...notesValidationRules?.description, onChange: (e) => { handleInputChange(e.target.name, e.target.value) } }
                                                    )}
                                                />
                                            </React.Fragment>
                                        )}
                                    </div>
                                </div>
                            </GridColumn>
                            <GridColumn sm={3}>
                                {loading ? (
                                    <Skeleton height="40px" variant="rectangle" />
                                ) : (
                                    <React.Fragment>
                                        <Label text={TAGS} />
                                        <TagAutocomplete
                                            placeholder={SELECT_TAGS}
                                            setValue={(v) => handleInputChange("tags", v)}
                                            tagData={noteTags?.masterData?.map(val => val?.value)}
                                        />
                                    </React.Fragment>
                                )}
                            </GridColumn>
                        </Grid>
                    </>
                )}
                {cancelNoteCreationModal()}
            </Detail>
        </>
    );
};

export default ViewInvoiceNotes;
